/**
 * AI Input Cleaners — Unified, config-driven data cleaning for AI models.
 *
 * Single source of truth per entity type.
 * Each entity has:
 *   - alwaysStrip: fields always removed (system/sensitive)
 *   - profiles: named whitelists for different use cases
 *   - defaultProfile: which profile to use when none specified
 *
 * Usage:
 *   cleanForAI('actor', rawActor)                    // default profile
 *   cleanForAI('actor', rawActor, 'insights')         // named profile
 *   cleanForAI('actor', rawActor, { fields: [...] })  // inline override
 */

// ── Types ────────────────────────────────────────────────────────────

export interface CleanProfile {
  /** Fields to keep (whitelist) */
  fields: string[];
  /** Nested entity → profile name mapping for array fields */
  nested?: Record<string, string>;
}

interface EntityCleanerConfig {
  /** Fields always stripped regardless of profile */
  alwaysStrip: string[];
  /** Named cleaning profiles */
  profiles: Record<string, CleanProfile>;
  /** Default profile name */
  defaultProfile: string;
  /** Map field name → nested entity type (for recursive cleaning) */
  nestedFields?: Record<string, string>;
}

/** All registered entity type names */
export type EntityType = keyof typeof CLEANER_CONFIGS;

// ── Helpers ──────────────────────────────────────────────────────────

function isFirestoreTimestamp(value: any): boolean {
  return (
    value !== null &&
    typeof value === 'object' &&
    value.type === 'firestore/timestamp/1.0' &&
    'seconds' in value
  );
}

/**
 * Recursively remove Firestore timestamp objects from nested structures.
 * Does NOT remove named timestamp fields (those are handled by alwaysStrip).
 */
export function stripTimestampObjects(obj: any): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripTimestampObjects);

  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (isFirestoreTimestamp(value)) continue;
    result[key] = stripTimestampObjects(value);
  }
  return result;
}

// ── JTBD array field names (shared constant) ────────────────────────

const JTBD_FIELDS = [
  'coreJobs', 'contexts', 'fears', 'bigJobs',
  'subtasks', 'competitors', 'habits',
] as const;

// ── Config Registry ─────────────────────────────────────────────────

const CLEANER_CONFIGS = {
  insightItem: {
    alwaysStrip: ['order'],
    profiles: {
      full:     { fields: ['id', 'description', 'weight', 'fullPhraseFromInterview', 'forceTag', 'pyramidLevel'] },
      // 'standard' carries forceTag + pyramidLevel through to InsightsAnalysisHandler
      // so D2 tags survive cleanForAI('actor', _, 'insights'). Empty in B2C, populated in B2B.
      standard: { fields: ['id', 'description', 'weight', 'forceTag', 'pyramidLevel'] },
      minimal:  { fields: ['description'] },
    },
    defaultProfile: 'standard',
  },

  actor: {
    alwaysStrip: ['fullInterviewResult', 'creationDate', 'createdAt', 'updatedAt'],
    profiles: {
      strategy: {
        // role + influence are B2B-only optional fields on Actor; keep them so
        // strategy/content-plan AI calls can reason about DMU stakeholder mix.
        fields: ['id', 'name', 'description', 'weight', 'role', 'influence',
                 ...JTBD_FIELDS, 'taskId', 'taskIds'],
        nested: { insightItem: 'standard' },
      },
      insights: {
        fields: ['id', 'name', 'description', 'audienceId', 'audienceName', 'weight',
                 'role', 'influence',
                 ...JTBD_FIELDS, 'taskId', 'taskName', 'taskIds'],
        // W13-R3: 'full' (= 'standard' + fullPhraseFromInterview) — the frozen
        // insights input keeps the verbatim phrase so enrichment can quote it
        // (InsightSource.originalPhrase). The aggregation user message still
        // serializes only {id, description[, tags]} per item, so D41 holds.
        nested: { insightItem: 'full' },
      },
      minimal: {
        fields: ['name', 'description', 'role', ...JTBD_FIELDS],
        nested: { insightItem: 'minimal' },
      },
      // Content plan generator — keeps full JTBD + audience linkage so AI
      // can answer per-audience "why this post matters" (WhyForAudience).
      contentPlan: {
        fields: ['id', 'name', 'description', 'audienceId', 'audienceName', 'weight',
                 'role', 'influence',
                 ...JTBD_FIELDS, 'taskId', 'taskIds'],
        nested: { insightItem: 'standard' },
      },
    },
    defaultProfile: 'strategy',
    nestedFields: Object.fromEntries(JTBD_FIELDS.map(f => [f, 'insightItem' as const])),
  },

  audience: {
    alwaysStrip: ['creationDate', 'updatedAt', 'taskStatus', 'taskContext', 'weight'],
    profiles: {
      standard: {
        // dmuMode + icpProfile are B2B-only optional fields; keep them so
        // strategy/content-plan AI calls can reason about ICP archetypes.
        fields: ['id', 'name', 'description', 'actors', 'taskId', 'taskIds',
                 'dmuMode', 'icpProfile'],
        nested: { actor: 'strategy' },
      },
      insights: {
        fields: ['id', 'name', 'description', 'actors',
                 'dmuMode', 'icpProfile'],
        nested: { actor: 'insights' },
      },
      // Content plan: same shape as insights but with taskIds (so the AI can
      // attribute posts to specific task-level seed entities).
      contentPlan: {
        fields: ['id', 'name', 'description', 'actors', 'taskId', 'taskIds',
                 'dmuMode', 'icpProfile'],
        nested: { actor: 'contentPlan' },
      },
    },
    defaultProfile: 'standard',
    nestedFields: { actors: 'actor' as const },
  },

  task: {
    alwaysStrip: ['createdAt', 'updatedAt'],
    profiles: {
      standard: {
        fields: ['id', 'name', 'description', 'funnelGoals', 'requiredInfo',
                 'requiredTimeMinutes', 'priority', 'userTrustLevel',
                 'competitorsForClients', 'userCondition'],
      },
      minimal: {
        fields: ['id', 'name', 'description'],
      },
      // Content plan: tasks are seed post ideas — keep full context so AI
      // can write posts explaining why a specific task matters per audience.
      contentPlan: {
        fields: ['id', 'name', 'description', 'funnelGoals',
                 'priority', 'competitorsForClients'],
      },
    },
    defaultProfile: 'standard',
  },

  project: {
    alwaysStrip: [
      'creationDate', 'updatedAt', 'defaultAudienceCount',
      'taskStatus', 'taskType', 'paymentProvider', 'stripeConfig',
      'paddleConfig', 'members', 'invitedUsers', 'backend_tasks',
    ],
    profiles: {
      strategy: {
        // audienceModel is B2B-only optional; expose so strategy AI knows
        // whether it's planning for B2C or B2B audiences.
        fields: ['name', 'description', 'url', 'platform', 'toneOfVoice',
                 'designDescription', 'category', 'previousUserStepDescription',
                 'audienceModel'],
      },
      interview: {
        fields: ['name', 'description', 'url', 'platform', 'category',
                 'interviewQuestions', 'audienceModel'],
      },
      full: {
        fields: ['id', 'name', 'description', 'url', 'platform', 'toneOfVoice',
                 'designDescription', 'category', 'previousUserStepDescription',
                 'interviewQuestions', 'targetCountries', 'trafficChannels',
                 'audienceModel'],
      },
      // Content plan: keeps brand voice + targetCountries (geographic/cultural
      // grounding affects tone) but strips interviewQuestions (onboarding-only).
      contentPlan: {
        fields: ['name', 'description', 'url', 'platform', 'toneOfVoice',
                 'designDescription', 'category', 'targetCountries',
                 'previousUserStepDescription', 'audienceModel'],
      },
    },
    defaultProfile: 'strategy',
  },

  block: {
    alwaysStrip: [
      'entityId', 'firstScreenId', 'position', 'isFirstBlock',
      'isLaunchBlock', 'functionMapping', 'createdAt',
    ],
    profiles: {
      standard: {
        fields: ['id', 'blockName', 'name', 'blockType', 'description',
                 'additionalInfo', 'action', 'order', 'taskId'],
      },
      withData: {
        fields: ['id', 'blockName', 'name', 'blockType', 'description',
                 'additionalInfo', 'action', 'order', 'taskId',
                 'sourceDataMapping', 'dataMapping', 'screens'],
      },
    },
    defaultProfile: 'standard',
  },

  review: {
    alwaysStrip: ['selectedTaskIds', 'createdAt'],
    profiles: {
      standard: { fields: ['nickname', 'rating', 'reviewtext', 'reviewdate', 'taskIds', 'audienceIds'] },
      withId:   { fields: ['id', 'nickname', 'rating', 'reviewtext', 'reviewdate', 'taskIds', 'audienceIds'] },
      // Content plan: with id so posts can cite specific reviews in dataUsage.
      contentPlan: { fields: ['id', 'nickname', 'rating', 'reviewtext', 'reviewdate', 'taskIds'] },
    },
    defaultProfile: 'standard',
  },

  rating: {
    alwaysStrip: ['createdAt'],
    profiles: {
      standard: { fields: ['rating', 'ratingCount', 'platform', 'description'] },
      strategy: { fields: ['id', 'rating', 'ratingCount', 'platform', 'description', 'taskIds', 'audienceIds'] },
      contentPlan: { fields: ['rating', 'ratingCount', 'platform', 'description'] },
    },
    defaultProfile: 'standard',
  },

  bigNumber: {
    alwaysStrip: ['selectedTaskIds', 'createdAt'],
    profiles: {
      standard: { fields: ['description', 'amount', 'taskIds', 'audienceIds'] },
      withId:   { fields: ['id', 'description', 'amount', 'unit', 'units', 'taskIds', 'audienceIds'] },
      contentPlan: { fields: ['id', 'description', 'amount', 'taskIds'] },
    },
    defaultProfile: 'standard',
  },

  studyCase: {
    alwaysStrip: [
      'selectedTaskIds', 'createdAt', 'minTheses',
      'preferredSources', 'maxStudyAgeYears', 'outputLanguage',
    ],
    profiles: {
      standard: { fields: ['featureName', 'featureDescription', 'primaryBenefit', 'outputs', 'taskIds', 'audienceIds'] },
      withId:   { fields: ['id', 'featureName', 'featureDescription', 'primaryBenefit', 'outputs', 'taskIds', 'audienceIds'] },
      contentPlan: { fields: ['id', 'featureName', 'featureDescription', 'primaryBenefit', 'outputs', 'taskIds'] },
    },
    defaultProfile: 'standard',
  },

  insightsAnalysis: {
    alwaysStrip: [
      'createdAt', 'updatedAt', 'status', 'projectId',
      'selectedActorRefs', 'affinityRules', 'identificationMatrix',
      'discriminativeQuestions',
    ],
    profiles: {
      strategy: { fields: ['id', 'name', 'summary', 'aggregatedInsights'] },
      minimal:  { fields: ['summary', 'aggregatedInsights'] },
      // Content plan: includes discriminativeQuestions (re-overrides alwaysStrip
      // safety — see contentPlanOverrides at bottom) so topics can cite specific
      // audience questions as seed post ideas.
      contentPlan: {
        fields: [
          'id', 'name', 'summary', 'mode', 'aggregatedInsights',
          'audienceBuyingMechanics',
          'audienceContentBriefs',
          'audienceReadinessProfiles',
        ],
      },
    },
    defaultProfile: 'strategy',
  },

  aggregatedInsight: {
    alwaysStrip: [
      'actorAffinity', 'informationGain',
      'discriminativePower', 'splittingEfficiency', 'userFrictionScore',
      'netValue', 'selectingActors', 'rejectingActors',
    ],
    profiles: {
      standard: { fields: ['id', 'category', 'canonicalDescription', 'cumulativeWeight', 'frequency', 'questionText'] },
      minimal:  { fields: ['canonicalDescription', 'cumulativeWeight'] },
      contentPlan: {
        fields: [
          'id', 'category', 'canonicalDescription', 'cumulativeWeight',
          'frequency', 'questionText', 'dominantForceTag',
          'dominantPyramidLevel', 'forceDistribution', 'sources',
        ],
      },
    },
    defaultProfile: 'standard',
  },

  // ── Content plan — new entity types ──────────────────────────────

  funnelVariable: {
    alwaysStrip: ['createdAt', 'updatedAt'],
    profiles: {
      contentPlan: {
        fields: ['id', 'label', 'type', 'options', 'boundScreenIds',
                 'dataUsage', 'description', 'tags'],
      },
      minimal: {
        fields: ['id', 'label', 'options'],
      },
    },
    defaultProfile: 'contentPlan',
  },

  contentTopic: {
    // For PriorContextBundle.posts summaries + backlog-aware prompts — we
    // send condensed topic references to the AI, never the full topic object.
    alwaysStrip: [
      'createdAt', 'updatedAt', 'usedInSprintIds', 'usedInPostIds',
      'authorUserId', 'userNotes', 'generationBriefId',
    ],
    profiles: {
      priorContext: {
        fields: ['id', 'angle', 'themeAssignment', 'tags', 'publishedAt', 'status'],
      },
      backlogSummary: {
        fields: ['id', 'angle', 'summary', 'themeAssignment', 'tags'],
      },
      full: {
        fields: ['id', 'projectId', 'status', 'angle', 'summary', 'formatHint',
                 'themeAssignment', 'tags', 'weight', 'dataUsage',
                 'groundingResearchPackIds', 'targetAudienceIds', 'targetPlatforms',
                 'targetDmuRoles', 'targetForceTags', 'targetPyramidLevels',
                 'buyingSequencePosition',
                 'preferredSprintTypes', 'dedupFingerprint'],
      },
    },
    defaultProfile: 'full',
  },

  contentPost: {
    // For PriorContextBundle (dedup feed) — we never send full body to AI;
    // hook + fingerprint + theme coverage is enough for steering.
    alwaysStrip: [
      'createdAt', 'updatedAt', 'body', 'cta', 'aiAssets',
      'sourcesCited', 'targetMetrics', 'failureReason',
    ],
    profiles: {
      priorContext: {
        fields: ['id', 'topicId', 'platform', 'hook', 'dedupFingerprint',
                 'scheduledAt', 'publishedAt', 'status'],
      },
      full: {
        fields: ['id', 'projectId', 'strategyId', 'sprintId', 'topicId',
                 'platform', 'weekIndex', 'weekdayIndex', 'scheduledAt',
                 'format', 'hook', 'dataUsage', 'whyForAudience',
                 'whyForDmuRole', 'targetAudienceId', 'targetDmuRole',
                 'targetForceTag', 'targetPyramidLevel',
                 'buyingSequencePosition',
                 'dedupFingerprint', 'status'],
      },
    },
    defaultProfile: 'priorContext',
  },
} satisfies Record<string, EntityCleanerConfig>;

// ── Core Cleaner ────────────────────────────────────────────────────

/**
 * Clean entity data for AI consumption.
 *
 * @param entityType — registered entity name
 * @param data — raw entity data
 * @param profileOrOverride — profile name string or inline CleanProfile
 * @returns cleaned data with only whitelisted fields
 */
export function cleanForAI(
  entityType: EntityType,
  data: any,
  profileOrOverride?: string | CleanProfile,
): any {
  if (!data || typeof data !== 'object') return data;

  const config: EntityCleanerConfig = CLEANER_CONFIGS[entityType];
  if (!config) return data;

  // Resolve profile
  let profile: CleanProfile;
  if (typeof profileOrOverride === 'object' && profileOrOverride !== null) {
    profile = profileOrOverride;
  } else {
    const profileName = (typeof profileOrOverride === 'string' ? profileOrOverride : null)
      ?? config.defaultProfile;
    profile = (config.profiles as Record<string, CleanProfile>)[profileName]
      ?? (config.profiles as Record<string, CleanProfile>)[config.defaultProfile];
  }

  const alwaysStripSet = new Set(config.alwaysStrip);
  const result: any = {};

  for (const field of profile.fields) {
    // Safety: never include always-stripped fields even if profile lists them
    if (alwaysStripSet.has(field)) continue;
    if (data[field] === undefined) continue;

    const value = data[field];

    // Handle nested entity arrays
    if (Array.isArray(value)) {
      const nestedType = config.nestedFields?.[field as string] as EntityType | undefined;
      const nestedProfile = profile.nested?.[nestedType as string];

      if (nestedType && nestedProfile) {
        result[field] = value.map(item => cleanForAI(nestedType, item, nestedProfile));
        continue;
      }
    }

    // Clean nested objects from Firestore timestamp artifacts
    result[field] = typeof value === 'object' ? stripTimestampObjects(value) : value;
  }

  return result;
}

// ── Composite Cleaners ──────────────────────────────────────────────

function filterInsightsBySelectedIds(analyses: any[], insightIds?: unknown): any[] {
  const selectedInsightIds = new Set(
    (Array.isArray(insightIds) ? insightIds : [])
      .map((id) => typeof id === 'string' ? id.trim() : '')
      .filter(Boolean),
  );
  if (selectedInsightIds.size === 0) return analyses;
  return analyses.filter((analysis: any) => {
    const id = typeof analysis?.id === 'string' ? analysis.id : '';
    return id && selectedInsightIds.has(id);
  });
}

function filterB2BInsightsForSelectedAudiences(
  cleanedSource: any,
  selectedAudienceBriefRefs?: Array<{ analysisId?: string; audienceId?: string }>,
): any[] {
  const selectedRefPairs = new Set(
    (selectedAudienceBriefRefs ?? [])
      .map((ref) => {
        const analysisId = typeof ref?.analysisId === 'string' ? ref.analysisId.trim() : '';
        const audienceId = typeof ref?.audienceId === 'string' ? ref.audienceId.trim() : '';
        return analysisId && audienceId ? `${analysisId}::${audienceId}` : '';
      })
      .filter(Boolean),
  );
  const selectedAudienceIdsFromRefs = new Set(
    (selectedAudienceBriefRefs ?? [])
      .map((ref) => typeof ref?.audienceId === 'string' ? ref.audienceId.trim() : '')
      .filter(Boolean),
  );
  const selectedAudienceIds = selectedAudienceIdsFromRefs.size > 0
    ? selectedAudienceIdsFromRefs
    : new Set(
      (cleanedSource.audiences ?? [])
        .map((audience: any) => typeof audience?.id === 'string' ? audience.id : undefined)
        .filter(Boolean),
    );
  const analyses = Array.isArray(cleanedSource.insights) ? cleanedSource.insights : [];
  if (selectedAudienceIds.size === 0) return analyses;

  return analyses.map((analysis: any) => {
    if (!analysis || typeof analysis !== 'object') return analysis;
    const analysisId = typeof analysis.id === 'string'
      ? analysis.id
      : typeof analysis.analysisId === 'string'
        ? analysis.analysisId
        : '';
    const filterByAudience = (items: unknown) => {
      if (!Array.isArray(items)) return items;
      return items.filter((item: any) => {
        const audienceId = typeof item?.audienceId === 'string' ? item.audienceId : '';
        if (!audienceId) return selectedRefPairs.size === 0;
        if (selectedRefPairs.size > 0) {
          return selectedRefPairs.has(`${analysisId}::${audienceId}`);
        }
        return selectedAudienceIds.has(audienceId);
      });
    };
    return {
      ...analysis,
      audienceBuyingMechanics: filterByAudience(analysis.audienceBuyingMechanics),
      audienceContentBriefs: filterByAudience(analysis.audienceContentBriefs),
      audienceReadinessProfiles: filterByAudience(analysis.audienceReadinessProfiles),
    };
  });
}

/**
 * Clean full sourceData object for strategy AI calls.
 * Replaces cleanStrategyPayload() from sourceDataCleaner.ts.
 *
 * @param sourceData — raw source data with audiences, tasks, social proof
 * @param actorProfile — which actor profile to use (default: 'strategy')
 */
export function cleanSourceDataForAI(
  sourceData: any,
  actorProfile: string = 'strategy',
): any {
  return {
    audiences: (sourceData.audiences ?? []).map((a: any) =>
      cleanForAI('audience', a, {
        fields: ['id', 'name', 'description', 'actors', 'taskId', 'taskIds'],
        nested: { actor: actorProfile },
      }),
    ),
    tasks: (sourceData.tasks ?? []).map((t: any) => cleanForAI('task', t)),
    reviews: (sourceData.reviews ?? []).map((r: any) => cleanForAI('review', { ...r, taskIds: r.taskIds ?? r.selectedTaskIds }, 'withId')),
    studyCases: (sourceData.studyCases ?? []).map((s: any) => cleanForAI('studyCase', { ...s, taskIds: s.taskIds ?? s.selectedTaskIds }, 'withId')),
    bigNumbers: (sourceData.bigNumbers ?? []).map((b: any) => cleanForAI('bigNumber', { ...b, taskIds: b.taskIds ?? b.selectedTaskIds }, 'withId')),
    ratings: (sourceData.ratings ?? []).map((r: any) => cleanForAI('rating', { ...r, taskIds: r.taskIds ?? r.selectedTaskIds }, 'strategy')),
    insights: (sourceData.insights?.analyses ?? []).map((a: any) => {
      const cleaned = cleanForAI('insightsAnalysis', a);
      // Deep-clean individual insight items within aggregatedInsights
      if (cleaned.aggregatedInsights && typeof cleaned.aggregatedInsights === 'object') {
        const cleanedInsights: Record<string, any[]> = {};
        for (const [cat, items] of Object.entries(cleaned.aggregatedInsights)) {
          if (Array.isArray(items)) {
            cleanedInsights[cat] = items.map((item: any) => cleanForAI('aggregatedInsight', item));
          }
        }
        cleaned.aggregatedInsights = cleanedInsights;
      }
      return cleaned;
    }),
  };
}

/**
 * Clean full sourceData object for content plan AI calls.
 *
 * Parallel of cleanSourceDataForAI() but routes every entity through its
 * 'contentPlan' profile. Used by ContentStrategyHandler, TopicGeneratorHandler,
 * SprintPlannerHandler, and post writer handlers.
 *
 * Variables are passed alongside sourceData (not nested) — they are a distinct
 * input at the content-plan level, mirroring how strategyVariables work in
 * BlockScreenHandler.
 *
 * @param sourceData — raw source data with audiences, tasks, reviews, insights, etc.
 * @param variables — optional variables map, each cleaned via funnelVariable/contentPlan
 * @returns cleaned source data ready for content-plan AI consumption
 */
export function cleanSourceDataForContentPlan(
  sourceData: any,
  variables?: Record<string, any>,
  options?: {
    sourceDataIds?: { insightIds?: string[] };
    selectedAudienceBriefRefs?: Array<{ analysisId?: string; audienceId?: string }>;
  },
): any {
  const cleaned = {
    audiences: (sourceData.audiences ?? []).map((a: any) =>
      cleanForAI('audience', a, 'contentPlan'),
    ),
    tasks: (sourceData.tasks ?? []).map((t: any) => cleanForAI('task', t, 'contentPlan')),
    reviews: (sourceData.reviews ?? []).map((r: any) => cleanForAI('review', r, 'contentPlan')),
    studyCases: (sourceData.studyCases ?? []).map((s: any) => cleanForAI('studyCase', s, 'contentPlan')),
    bigNumbers: (sourceData.bigNumbers ?? []).map((b: any) => cleanForAI('bigNumber', b, 'contentPlan')),
    ratings: (sourceData.ratings ?? []).map((r: any) => cleanForAI('rating', r, 'contentPlan')),
    insights: (sourceData.insights?.analyses ?? []).map((a: any) => {
      const cleaned = cleanForAI('insightsAnalysis', a, 'contentPlan');
      if (cleaned.aggregatedInsights && typeof cleaned.aggregatedInsights === 'object') {
        const cleanedInsights: Record<string, any[]> = {};
        for (const [cat, items] of Object.entries(cleaned.aggregatedInsights)) {
          if (Array.isArray(items)) {
            cleanedInsights[cat] = items.map((item: any) =>
              cleanForAI('aggregatedInsight', item, 'contentPlan'),
            );
          }
        }
        cleaned.aggregatedInsights = cleanedInsights;
      }
      return cleaned;
    }),
    variables: variables
      ? Object.fromEntries(
          Object.entries(variables).map(([key, v]) => [
            key,
            cleanForAI('funnelVariable', v, 'contentPlan'),
          ]),
        )
      : undefined,
  };
  cleaned.insights = filterInsightsBySelectedIds(cleaned.insights, options?.sourceDataIds?.insightIds);
  cleaned.insights = filterB2BInsightsForSelectedAudiences(cleaned, options?.selectedAudienceBriefRefs);
  return cleaned;
}

/**
 * Clean an array of actors for insights analysis.
 * Replaces cleanActorPayload() from cleanInput.ts.
 *
 * @param actors — raw actor data with JTBD subcollections
 * @returns cleaned actors + reduction stats
 */
export function cleanActorsForInsights(
  actors: any[],
): { cleaned: any[]; stats: { originalSize: number; cleanedSize: number; reduction: string; totalInsightItems: number } } {
  const originalJson = JSON.stringify(actors);
  const cleaned = actors.map(a => cleanForAI('actor', a, 'insights'));
  const cleanedJson = JSON.stringify(cleaned);

  const totalInsightItems = cleaned.reduce((sum, a) => {
    return sum + JTBD_FIELDS.reduce((s, key) => s + (a[key]?.length ?? 0), 0);
  }, 0);

  return {
    cleaned,
    stats: {
      originalSize: originalJson.length,
      cleanedSize: cleanedJson.length,
      reduction: `${((1 - cleanedJson.length / originalJson.length) * 100).toFixed(1)}%`,
      totalInsightItems,
    },
  };
}

// ── Exports for config introspection ────────────────────────────────

/** Get available profiles for an entity type */
export function getProfiles(entityType: EntityType): string[] {
  return Object.keys(CLEANER_CONFIGS[entityType]?.profiles ?? {});
}

/** Get the config for inspection/debugging */
export function getConfig(entityType: EntityType): EntityCleanerConfig | undefined {
  return CLEANER_CONFIGS[entityType];
}
